if (!customElements.get('compare-products')) {
  const PUMP_AXES = [
    {
      key: 'delivery',
      values: [
        { value: 'Carb', label: 'Carb' },
        { value: 'EFI', label: 'EFI' },
      ],
    },
    {
      key: 'induction',
      values: [
        { value: 'Naturally Aspirated', label: 'Naturally Aspirated' },
        { value: 'Forced Induction', label: 'Forced Induction' },
      ],
    },
    {
      key: 'fuel',
      values: [
        { value: 'Gas', label: 'Gasoline' },
        { value: 'E85', label: 'E85' },
      ],
    },
  ];

  customElements.define(
    'compare-products',
    class CompareProducts extends HTMLElement {
      connectedCallback() {
        this.storageKey = 'aeromotive:compare';
        this.board = this.querySelector('[data-comparison-board]');
        this.headers = this.querySelector('[data-comparison-headers]');
        this.specs = this.querySelector('[data-comparison-specs]');
        this.emptyMessage = this.querySelector('[data-comparison-empty]');
        this.expandWrapper = this.querySelector('[data-comparison-expand-wrapper]');
        this.rowsVisible = Number(this.dataset.rowsVisible) || 12;
        this.maxProducts = Number(this.dataset.maxProducts) || 3;
        this.optionNames = (this.dataset.optionNames || '')
          .split(',')
          .map((name) => name.trim().toLowerCase())
          .filter(Boolean);
        this.tooltips = this.readTooltips();

        const handles = this.readHandles();
        if (handles.length < 2) return;

        if (this.merchandised && 'IntersectionObserver' in window) {
          this.columnObserver = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.columnObserver.disconnect();
              this.loadColumns(handles);
            },
            { rootMargin: '400px' }
          );
          this.columnObserver.observe(this);
          return;
        }

        this.loadColumns(handles);
      }

      disconnectedCallback() {
        if (this.columnObserver) this.columnObserver.disconnect();
        if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
        if (this.noticeTimeout) clearTimeout(this.noticeTimeout);
        if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
        if (this.onWindowScroll) window.removeEventListener('scroll', this.onWindowScroll);
        if (this.onViewportChange) window.removeEventListener('resize', this.onViewportChange);
        if (this.onBoardScroll) this.board.removeEventListener('scroll', this.onBoardScroll);
      }

      readStrings() {
        try {
          return JSON.parse(this.dataset.characteristicLabels || '{}');
        } catch (error) {
          return {};
        }
      }

      readTooltips() {
        const script = this.querySelector('[data-comparison-tooltips]');
        try {
          return JSON.parse(script.textContent) || {};
        } catch (error) {
          return {};
        }
      }

      readHandles() {
        this.merchandised = this.hasAttribute('data-handles');
        if (this.merchandised) {
          return this.dataset.handles
            .split(',')
            .map((handle) => handle.trim())
            .filter(Boolean)
            .slice(0, this.maxProducts);
        }

        const param = new URLSearchParams(window.location.search).get('products');
        if (param) {
          return param
            .split(',')
            .map((handle) => decodeURIComponent(handle.trim()))
            .filter(Boolean)
            .slice(0, this.maxProducts);
        }

        try {
          const stored = JSON.parse(window.localStorage.getItem(this.storageKey)) || [];
          return stored.map((item) => item.handle).filter(Boolean).slice(0, this.maxProducts);
        } catch (error) {
          return [];
        }
      }

      loadColumns(handles) {
        const base = this.dataset.collectionsUrl.replace(/collections\/all\/?$/, '');

        Promise.all(
          handles.map((handle) =>
            fetch(`${base}products/${encodeURIComponent(handle)}?view=compare-column`)
              .then((response) => (response.ok ? response.text() : null))
              .catch(() => null)
          )
        ).then((results) => {
          const parser = new DOMParser();

          results.forEach((html) => {
            if (!html) return;
            const column = parser.parseFromString(html, 'text/html').querySelector('[data-comparison-column]');
            if (!column) return;
            column.dataset.column = String(this.headers.children.length);
            this.headers.appendChild(column);
          });

          if (this.headers.children.length < 2) {
            if (this.merchandised) this.hidden = true;
            return;
          }

          this.emptyMessage.hidden = true;
          this.board.hidden = false;
          this.setup();
        });
      }

      setup() {
        this.columns = Array.from(this.headers.querySelectorAll('[data-comparison-column]'));
        if (this.merchandised) this.columns.forEach((column) => this.removeToggle(column));
        this.columns.forEach((column) => this.trimOptions(column));
        this.columnData = this.columns.map((column) => {
          try {
            return JSON.parse(column.querySelector('[data-comparison-data]').textContent);
          } catch (error) {
            return { variants: [], specs: {}, ratings: {} };
          }
        });

        this.strings = this.readStrings();
        this.render();
      }

      removeToggle(column) {
        const toggle = column.querySelector('[data-comparison-toggle]');
        if (toggle) toggle.closest('.product-comparison__toggle').remove();
      }

      trimOptions(column) {
        if (!this.optionNames.length) return;

        const fields = Array.from(column.querySelectorAll('[data-comparison-option-field]'));
        if (!fields.length) return;

        const kept = fields.filter((field) => {
          const name = field.dataset.optionName.toLowerCase();
          return this.optionNames.some((allowed) => name.includes(allowed));
        });
        const dropped = fields.filter((field) => !kept.includes(field));
        if (!dropped.length) return;

        const sku = column.querySelector('[data-comparison-sku]');
        if (sku && dropped.some((field) => field.contains(sku))) {
          if (kept.length) {
            kept[0].querySelector('[data-comparison-option-head]').appendChild(sku);
          } else {
            sku.classList.add('product-comparison__sku--standalone');
            column.querySelector('[data-comparison-details]').appendChild(sku);
          }
        }

        dropped.forEach((field) => field.remove());
        if (kept.length) return;

        const options = column.querySelector('[data-comparison-options]');
        if (options) options.remove();
      }

      render() {
        this.buildCharacteristics();
        this.labels = this.buildLabels();
        this.renderSpecs();
        this.cells = Array.from(this.specs.querySelectorAll('[data-comparison-cell]'));

        this.columns.forEach((_, index) => this.applyVariant(index, this.currentVariant(index)));

        this.bindColumns();
        this.bindHighlight();
        this.bindExpand();
        this.bindTooltips();
        this.bindStickyHeader();
        this.bindNotice();
        this.refresh();
      }

      buildCharacteristics() {
        const values = {};

        PUMP_AXES.forEach(({ key, values: known }) => {
          const published = [];

          this.columnData.forEach((data) => {
            Object.keys(data.ratings || {}).forEach((variantId) => {
              (data.ratings[variantId] || []).forEach((rating) => {
                if (rating[key] && !published.includes(rating[key])) published.push(rating[key]);
              });
            });
          });

          const ordered = known.filter((option) => published.includes(option.value));
          const unknown = published
            .filter((value) => !known.some((option) => option.value === value))
            .map((value) => ({ value, label: value }));

          values[key] = [...ordered, ...unknown];
        });

        this.axes = PUMP_AXES.map(({ key }) => key);
        this.characteristicValues = values;
        this.hasHorsepower = this.axes.every((key) => values[key].length > 0);
        if (!this.hasHorsepower) return;

        this.selected = {};
        this.axes.forEach((key) => {
          this.selected[key] = values[key][0].value;
        });

        this.renderCharacteristics();
      }

      renderCharacteristics() {
        const wrapper = this.querySelector('[data-comparison-characteristics]');
        const fields = this.querySelector('[data-comparison-characteristics-fields]');
        if (!wrapper || !fields) return;

        const labels = this.strings;

        fields.textContent = '';

        this.axes.forEach((key) => {
          const options = this.characteristicValues[key];
          if (!options.length) return;

          const field = document.createElement('div');
          field.className = 'product-comparison__characteristic';

          const id = `ComparisonCharacteristic-${key}`;
          const label = document.createElement('label');
          label.className = 'product-comparison__characteristic-label';
          label.setAttribute('for', id);
          label.textContent = `${labels[key] || key}:`;

          const selectWrap = document.createElement('div');
          selectWrap.className = 'product-comparison__characteristic-select';

          const select = document.createElement('select');
          select.id = id;
          select.dataset.comparisonCharacteristic = key;
          options.forEach(({ value, label: text }) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = text;
            select.appendChild(option);
          });
          select.value = this.selected[key];
          select.addEventListener('change', () => {
            this.selected[key] = select.value;
            this.refreshHorsepower();
          });

          selectWrap.append(select, this.caretIcon());
          field.append(label, selectWrap);
          fields.appendChild(field);
        });

        wrapper.hidden = false;
      }

      caretIcon() {
        const holder = document.createElement('span');
        holder.innerHTML =
          '<svg class="icon icon-caret" aria-hidden="true" focusable="false" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>';
        return holder.firstChild;
      }

      variantRatings(index) {
        const variant = this.currentVariant(index);
        const ratings = this.columnData[index].ratings || {};
        return (variant && ratings[String(variant.id)]) || [];
      }

      horsepowerFor(columnIndex, bound) {
        if (!this.selected) return null;

        const match = this.variantRatings(columnIndex).find((rating) =>
          this.axes.every((key) => rating[key] === this.selected[key])
        );
        if (!match) return null;

        const value = match[bound];
        if (value === undefined || value === null || value === '') return null;

        return `${Number(value).toLocaleString()} hp`;
      }

      refreshHorsepower() {
        this.cells
          .filter((cell) => cell.dataset.horsepower)
          .forEach((cell) => {
            const value = cell.querySelector('[data-comparison-value]');
            const result = this.horsepowerFor(Number(cell.dataset.column), cell.dataset.horsepower);
            value.textContent = result || '—';
          });
        this.updateDifferences();
      }

      buildLabels() {
        const seen = new Set();
        const labels = [];

        if (this.hasHorsepower) {
          labels.push(
            { text: this.strings.minHp || 'Min HP', horsepower: 'min' },
            { text: this.strings.maxHp || 'Max HP', horsepower: 'max' }
          );
        }

        this.columnData.forEach((data) => {
          Object.keys(data.specs || {}).forEach((variantId) => {
            Object.keys(data.specs[variantId] || {}).forEach((label) => {
              if (seen.has(label)) return;
              seen.add(label);
              labels.push({ text: label });
            });
          });
        });

        return labels;
      }

      currentVariant(index) {
        const data = this.columnData[index];
        if (!data.variants.length) return null;

        const selects = Array.from(this.columns[index].querySelectorAll('[data-comparison-option]'));
        if (!selects.length) return data.variants[0];

        return (
          data.variants.find((variant) =>
            selects.every((select) => variant.options[Number(select.dataset.comparisonOption)] === select.value)
          ) || data.variants[0]
        );
      }

      specValue(index, label) {
        const variant = this.currentVariant(index);
        if (!variant) return '—';
        const map = this.columnData[index].specs[String(variant.id)] || {};
        const value = map[label];
        return value && value.length ? value : '—';
      }

      renderSpecs() {
        this.specs.textContent = '';

        this.labels.forEach((entry, rowIndex) => {
          const label = entry.text;
          this.columns.forEach((_, columnIndex) => {
            const cell = document.createElement('div');
            cell.className = 'product-comparison__cell';
            if (rowIndex === this.labels.length - 1) cell.classList.add('product-comparison__cell--last');
            if (rowIndex >= this.rowsVisible) cell.classList.add('product-comparison__cell--extra');
            cell.setAttribute('data-comparison-cell', '');
            cell.dataset.column = String(columnIndex);
            cell.dataset.row = String(rowIndex);
            if (entry.horsepower) cell.dataset.horsepower = entry.horsepower;

            const labelWrapper = document.createElement('span');
            labelWrapper.className = 'product-comparison__cell-label';

            const labelText = document.createElement('span');
            labelText.className = 'product-comparison__cell-label-text';
            labelText.textContent = label;
            labelWrapper.appendChild(labelText);

            if (this.tooltips[label]) {
              const trigger = document.createElement('button');
              trigger.type = 'button';
              trigger.className = 'product-comparison__info';
              trigger.setAttribute('aria-expanded', 'false');
              trigger.setAttribute('aria-label', label);
              trigger.setAttribute('data-comparison-info', '');
              trigger.innerHTML = this.infoIcon();
              labelWrapper.appendChild(trigger);

              const tooltip = document.createElement('span');
              tooltip.className = 'product-comparison__tooltip';
              tooltip.setAttribute('role', 'tooltip');
              tooltip.textContent = this.tooltips[label];
              labelWrapper.appendChild(tooltip);
            }

            const value = document.createElement('span');
            value.className = 'product-comparison__cell-value';
            value.dataset.comparisonValue = label;
            value.textContent = entry.horsepower
              ? this.horsepowerFor(columnIndex, entry.horsepower) || '—'
              : this.specValue(columnIndex, label);

            cell.append(labelWrapper, value);
            this.specs.appendChild(cell);
          });
        });

        if (this.labels.length > this.rowsVisible) this.expandWrapper.hidden = false;
      }

      infoIcon() {
        return '<svg class="icon icon-info" aria-hidden="true" focusable="false" viewBox="0 0 12 18" xmlns="http://www.w3.org/2000/svg" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M6 13C3.2385 13 1 10.7615 1 8C1 5.2385 3.2385 3 6 3C8.7615 3 11 5.2385 11 8C11 10.7615 8.7615 13 6 13ZM6 12.4C7.16695 12.4 8.28611 11.9364 9.11127 11.1113C9.93643 10.2861 10.4 9.16695 10.4 8C10.4 6.83305 9.93643 5.71389 9.11127 4.88873C8.28611 4.06357 7.16695 3.6 6 3.6C4.83305 3.6 3.71389 4.06357 2.88873 4.88873C2.06357 5.71389 1.6 6.83305 1.6 8C1.6 9.16695 2.06357 10.2861 2.88873 11.1113C3.71389 11.9364 4.83305 12.4 6 12.4ZM5.7 7H6.3V10.5H5.7V7ZM6 6.5C5.86739 6.5 5.74021 6.44732 5.64645 6.35355C5.55268 6.25979 5.5 6.13261 5.5 6C5.5 5.86739 5.55268 5.74021 5.64645 5.64645C5.74021 5.55268 5.86739 5.5 6 5.5C6.13261 5.5 6.25979 5.55268 6.35355 5.64645C6.44732 5.74021 6.5 5.86739 6.5 6C6.5 6.13261 6.44732 6.25979 6.35355 6.35355C6.25979 6.44732 6.13261 6.5 6 6.5Z" fill="#888A8C"/></svg>';
      }

      bindColumns() {
        this.columns.forEach((column, index) => {
          column.querySelectorAll('[data-comparison-option]').forEach((select) => {
            select.addEventListener('change', () => this.onOptionChange(index));
          });

          const toggle = column.querySelector('[data-comparison-toggle]');
          if (!toggle) return;

          toggle.addEventListener('change', () => {
            if (!toggle.checked && this.visibleColumnCount() < 2) {
              toggle.checked = true;
              return;
            }
            this.refresh();
            this.syncSelection();
            if (toggle.checked) {
              this.hideNotice();
            } else {
              this.showNotice(index);
            }
          });
        });
      }

      bindHighlight() {
        const highlight = this.querySelector('[data-comparison-highlight]');
        if (!highlight) return;

        highlight.addEventListener('change', () => {
          this.board.classList.toggle('is-highlighting', highlight.checked);
        });
      }

      bindExpand() {
        const expand = this.querySelector('[data-comparison-expand]');
        if (!expand) return;

        const label = expand.querySelector('[data-comparison-expand-label]');

        expand.addEventListener('click', () => {
          const isExpanded = expand.getAttribute('aria-expanded') === 'true';
          expand.setAttribute('aria-expanded', String(!isExpanded));
          this.board.classList.toggle('is-expanded', !isExpanded);
          if (label) label.textContent = isExpanded ? expand.dataset.expandLabel : expand.dataset.collapseLabel;
          this.updateDifferences();
          if (isExpanded) this.scrollExpandIntoView(expand);
        });
      }

      scrollExpandIntoView(expand) {
        const rect = expand.getBoundingClientRect();
        const target = window.scrollY + rect.bottom - window.innerHeight + 24;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        window.scrollTo({ top: Math.max(target, 0), behavior: reduced ? 'auto' : 'smooth' });
      }

      bindTooltips() {
        const triggers = Array.from(this.querySelectorAll('[data-comparison-info]'));
        if (!triggers.length) return;

        triggers.forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            const isOpen = trigger.getAttribute('aria-expanded') === 'true';
            triggers.forEach((other) => other.setAttribute('aria-expanded', 'false'));
            trigger.setAttribute('aria-expanded', String(!isOpen));
          });
        });

        this.onDocumentClick = (event) => {
          if (this.contains(event.target)) return;
          triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        };
        document.addEventListener('click', this.onDocumentClick);

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        });
      }

      stickyOffset() {
        const header = document.getElementById('shopify-section-header');
        if (!header || !header.classList.contains('shopify-section-header-sticky')) return 0;
        if (header.classList.contains('shopify-section-header-hidden')) return 0;
        return Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      }

      bindStickyHeader() {
        this.headersWrap = this.querySelector('[data-comparison-headers-wrap]');
        this.headersViewport = this.querySelector('[data-comparison-headers-viewport]');
        if (!this.headersWrap || !this.headersViewport) return;

        this.desktopQuery = window.matchMedia('(min-width: 750px)');

        this.onWindowScroll = () => {
          if (this.stickyFrame) return;
          this.stickyFrame = requestAnimationFrame(() => {
            this.stickyFrame = null;
            this.syncStuck();
          });
        };
        window.addEventListener('scroll', this.onWindowScroll, { passive: true });

        this.onViewportChange = () => {
          this.syncStuck();
          if (!this.board.classList.contains('is-stuck')) return;
          this.headersWidth = this.specs.scrollWidth;
          this.positionStuckHeader();
        };
        window.addEventListener('resize', this.onViewportChange);

        this.onBoardScroll = () => {
          if (this.board.classList.contains('is-stuck')) this.positionStuckHeader();
        };
        this.board.addEventListener('scroll', this.onBoardScroll);

        this.syncStuck();
      }

      syncStuck() {
        const offset = this.stickyOffset();
        const scrolledPast = this.headersWrap.getBoundingClientRect().bottom <= offset;
        const rowsInView = this.board.getBoundingClientRect().bottom > offset;
        this.setStuck(scrolledPast && rowsInView);
        if (offset !== this.appliedOffset && this.board.classList.contains('is-stuck')) this.positionStuckHeader();
      }

      setStuck(stuck) {
        if (this.board.classList.contains('is-stuck') === stuck) return;

        if (stuck) {
          this.headersWrap.style.height = `${this.headersViewport.offsetHeight}px`;
          this.headersWidth = this.specs.scrollWidth;
          this.board.classList.add('is-stuck');
          this.positionStuckHeader();
        } else {
          this.board.classList.remove('is-stuck');
          this.headersWrap.style.height = '';
          this.headers.style.paddingLeft = '';
          this.headers.style.paddingRight = '';
          this.headers.style.width = '';
          this.headers.style.transform = '';
        }
      }

      positionStuckHeader() {
        this.appliedOffset = this.stickyOffset();
        this.board.style.setProperty('--comparison-sticky-top', `${this.appliedOffset}px`);

        if (this.desktopQuery.matches) {
          const rect = this.headersWrap.getBoundingClientRect();
          const viewport = document.documentElement.clientWidth;
          this.headers.style.width = '';
          this.headers.style.transform = '';
          this.headers.style.paddingLeft = `${Math.max(rect.left, 0)}px`;
          this.headers.style.paddingRight = `${Math.max(viewport - rect.right, 0)}px`;
          return;
        }

        const board = this.board.getBoundingClientRect();
        const inset = Number.parseFloat(getComputedStyle(this.board).paddingLeft) || 0;
        this.headers.style.paddingLeft = '';
        this.headers.style.paddingRight = '';
        this.headers.style.width = `${this.headersWidth}px`;
        this.headers.style.transform = `translateX(${board.left + inset - this.board.scrollLeft}px)`;
      }

      bindNotice() {
        this.notice = this.querySelector('[data-comparison-notice]');
        if (!this.notice) return;

        this.notice.querySelector('[data-comparison-notice-close]').addEventListener('click', () => this.hideNotice());

        this.notice.querySelector('[data-comparison-notice-action]').addEventListener('click', () => {
          const column = this.columns[this.removedColumnIndex];
          const toggle = column && column.querySelector('[data-comparison-toggle]');
          if (toggle) {
            toggle.checked = true;
            this.refresh();
            this.syncSelection();
          }
          this.hideNotice();
        });
      }

      showNotice(index) {
        if (!this.notice) return;

        const column = this.columns[index];
        const image = column.querySelector('.product-comparison__media img');
        const title = column.querySelector('.product-comparison__title-link');
        const noticeImage = this.notice.querySelector('[data-comparison-notice-image]');

        this.removedColumnIndex = index;
        this.notice.querySelector('[data-comparison-notice-product]').textContent = title
          ? title.textContent.trim()
          : '';

        if (image) {
          noticeImage.src = image.currentSrc || image.src;
          noticeImage.hidden = false;
        } else {
          noticeImage.hidden = true;
        }

        this.notice.hidden = false;
        clearTimeout(this.noticeTimeout);
        this.noticeTimeout = setTimeout(() => this.hideNotice(), 8000);
      }

      hideNotice() {
        if (!this.notice) return;
        clearTimeout(this.noticeTimeout);
        this.notice.hidden = true;
      }

      visibleColumnCount() {
        return this.columns.filter((column) => {
          const toggle = column.querySelector('[data-comparison-toggle]');
          return !toggle || toggle.checked;
        }).length;
      }

      visibleHandles() {
        return this.columns
          .filter((column) => {
            const toggle = column.querySelector('[data-comparison-toggle]');
            return !toggle || toggle.checked;
          })
          .map((column) => {
            const link = column.querySelector('[data-comparison-cta]');
            return link ? link.dataset.productUrl.split('/').pop() : '';
          })
          .filter(Boolean);
      }

      syncSelection() {
        if (this.merchandised) return;

        const handles = this.visibleHandles();

        try {
          const stored = JSON.parse(window.localStorage.getItem(this.storageKey)) || [];
          window.localStorage.setItem(
            this.storageKey,
            JSON.stringify(stored.filter((item) => handles.includes(item.handle)))
          );
        } catch (error) {
        }

        const url = new URL(window.location.href);
        url.searchParams.set('products', handles.join(','));
        window.history.replaceState({}, '', url);
      }

      onOptionChange(index) {
        const variant = this.currentVariant(index);
        if (!variant) return;
        this.applyVariant(index, variant);
        this.updateDifferences();
      }

      applyVariant(index, variant) {
        if (!variant) return;

        const column = this.columns[index];
        const data = this.columnData[index];

        const price = column.querySelector('[data-comparison-price]');
        if (price) price.textContent = variant.price;

        const sku = column.querySelector('[data-comparison-sku-value]');
        if (sku) sku.textContent = variant.sku || '';

        column.querySelectorAll('[data-comparison-option-field]').forEach((field) => {
          const select = field.querySelector('[data-comparison-option]');
          const value = field.querySelector('[data-comparison-option-value]');
          if (select && value) value.textContent = variant.options[Number(select.dataset.comparisonOption)] || '';
        });

        const availability = column.querySelector('[data-comparison-availability]');
        if (availability) {
          availability.classList.toggle('product-comparison__availability--out', !variant.available);
          const text = availability.querySelector('[data-comparison-availability-text]');
          if (text) {
            text.textContent = variant.available
              ? availability.dataset.inStockText
              : availability.dataset.outOfStockText;
          }
        }

        const cta = column.querySelector('[data-comparison-cta]');
        if (cta) {
          cta.href = data.hasOnlyDefaultVariant
            ? cta.dataset.productUrl
            : `${cta.dataset.productUrl}?variant=${variant.id}`;
        }

        const specs = data.specs[String(variant.id)] || {};
        this.cells
          .filter((cell) => cell.dataset.column === String(index))
          .forEach((cell) => {
            const value = cell.querySelector('[data-comparison-value]');
            if (!value) return;

            if (cell.dataset.horsepower) {
              value.textContent = this.horsepowerFor(index, cell.dataset.horsepower) || '—';
              return;
            }

            const specValue = specs[value.dataset.comparisonValue];
            value.textContent = specValue && specValue.length ? specValue : '—';
          });
      }

      refresh() {
        const hidden = new Set();

        this.columns.forEach((column, index) => {
          const toggle = column.querySelector('[data-comparison-toggle]');
          const isVisible = !toggle || toggle.checked;
          column.classList.toggle('is-column-hidden', !isVisible);
          if (!isVisible) hidden.add(String(index));
        });

        this.board.style.setProperty('--comparison-columns', String(this.columns.length - hidden.size));
        this.board.classList.toggle('is-locked', this.columns.length - hidden.size < 3);

        this.cells.forEach((cell) => {
          cell.classList.toggle('is-column-hidden', hidden.has(cell.dataset.column));
        });

        const leading = this.columns.findIndex((_, index) => !hidden.has(String(index)));
        this.cells.forEach((cell) => {
          cell.classList.toggle('is-leading', cell.dataset.column === String(leading));
        });

        this.updateDifferences();

        if (this.board.classList.contains('is-stuck')) {
          this.headersWidth = this.specs.scrollWidth;
          this.positionStuckHeader();
        }
      }

      updateDifferences() {
        const rows = new Map();

        this.cells.forEach((cell) => {
          if (cell.classList.contains('is-column-hidden')) {
            cell.classList.remove('has-difference');
            return;
          }
          const row = cell.dataset.row;
          if (!rows.has(row)) rows.set(row, []);
          rows.get(row).push(cell);
        });

        rows.forEach((cells) => {
          const values = cells.map((cell) => {
            const value = cell.querySelector('[data-comparison-value]');
            return value ? value.textContent.trim().toLowerCase() : '';
          });
          const differs = values.some((value) => value !== values[0]);
          cells.forEach((cell) => cell.classList.toggle('has-difference', differs));
        });
      }
    }
  );
}
